import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StoreIcon from '@mui/icons-material/Store';
import PeopleIcon from '@mui/icons-material/People';
import CategoryIcon from '@mui/icons-material/Category';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <AppBar 
      position="fixed" 
      elevation={0}
      sx={{ 
        borderRadius: 0,
        color: 'primary.main',
        // Liquid glass: vidro fosco
        background: scrolled
          ? 'rgba(255, 255, 255, 0.78)'
          : 'rgba(255, 255, 255, 0.72)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        boxShadow: scrolled ? '0 4px 24px rgba(0, 0, 0, 0.06)' : '0 1px 0 rgba(0, 0, 0, 0.04)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.8)',
        transition: 'all 0.3s ease-in-out',
        zIndex: 1300,
      }}
    >
      <Toolbar sx={{ py: 0, px: { xs: 2, md: 4 }, height: 64, minHeight: 64, overflow: 'visible' }}>
        <Box
          component={Link}
          to="/"
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            backgroundColor: 'transparent',
            overflow: 'visible',
            '&:hover .logo-img': {
              transform: 'scale(1.06)',
              filter: 'brightness(1.08)',
            },
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL || ''}/logo.png`}
            alt="Apuro"
            className="logo-img"
            sx={{
              height: 112,
              width: 'auto',
              maxWidth: 400,
              objectFit: 'contain',
              transition: 'transform 0.25s ease, filter 0.25s ease',
              backgroundColor: 'transparent',
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {user ? (
            <>
              {(user.role === 'merchant' || user.role === 'store_moderator') && (
                <Button
                  color="inherit"
                  component={Link}
                  to="/dashboard/merchant"
                  startIcon={<DashboardIcon />}
                    sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(15, 23, 42, 0.06)',
                      color: 'secondary.main',
                    },
                  }}
                >
                  Dashboard
                </Button>
              )}
              {user.role === 'admin' && (
                <>
                  <Button
                    color="inherit"
                    component={Link}
                    to="/dashboard/admin"
                    startIcon={<DashboardIcon />}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(15, 23, 42, 0.06)',
                        color: 'secondary.main',
                      },
                    }}
                  >
                    Admin
                  </Button>
                  <Button
                    color="inherit"
                    component={Link}
                    to="/admin/users"
                    startIcon={<PeopleIcon />}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(15, 23, 42, 0.06)',
                        color: 'secondary.main',
                      },
                    }}
                  >
                    Usuários
                  </Button>
                  <Button
                    color="inherit"
                    component={Link}
                    to="/admin/stores"
                    startIcon={<StoreIcon />}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(15, 23, 42, 0.06)',
                        color: 'secondary.main',
                      },
                    }}
                  >
                    Lojas
                  </Button>
                  <Button
                    color="inherit"
                    component={Link}
                    to="/admin/categories"
                    startIcon={<CategoryIcon />}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(15, 23, 42, 0.06)',
                        color: 'secondary.main',
                      },
                    }}
                  >
                    Categorias
                  </Button>
                </>
              )}
              <IconButton
                color="inherit"
                component={Link}
                to="/favorites"
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(15, 23, 42, 0.06)',
                    color: 'secondary.main',
                  },
                }}
              >
                <FavoriteIcon />
              </IconButton>
              <Button 
                color="inherit" 
                onClick={handleLogout}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(15, 23, 42, 0.06)',
                    color: 'secondary.main',
                  },
                }}
              >
                Sair
              </Button>
            </>
          ) : (
            <>
              <Button 
                color="inherit" 
                component={Link} 
                to="/login"
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(15, 23, 42, 0.06)',
                    color: 'secondary.main',
                  },
                }}
              >
                Entrar
              </Button>
              <Button 
                variant="contained"
                component={Link} 
                to="/register"
                color="primary"
                sx={{
                  fontWeight: 600,
                  color: '#fff',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    color: '#fff',
                    transform: 'scale(1.03)',
                    boxShadow: '0 6px 20px rgba(15, 23, 42, 0.35)',
                  },
                }}
              >
                Cadastrar
              </Button>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
